import React, { memo } from 'react';
import PropTypes from 'prop-types';

import FileList from './FileList';
import AttachmentList from './AttachmentList';

/**
 * FilePicker component - A flexible file upload component with two display variants.
 *
 * Use `isAttachmentList={false}` (default) for a vertical list layout with grouping support.
 * Use `isAttachmentList={true}` for a horizontal scrollable layout.
 */
const FilePicker = memo(({ isAttachmentList = false, ...props }) => {
    if (isAttachmentList) {
        return <AttachmentList {...props} />;
    }
    return <FileList {...props} />;
});

FilePicker.displayName = 'FilePicker';

// Combine overrides from both variants
FilePicker.overrides = [
    // FileList overrides
    'root',
    'filePicker',
    'error',
    'info',
    'formControl',
    'Label',
    'groups',
    'groupsHeader',
    'groupsTitle',
    'file',
    'filesList',
    // AttachmentList overrides
    'Icon',
    'InputWrapper',
    'Loader',
    'Markdown',
    'Text',
    'Tooltip',
];

FilePicker.propTypes = {
    // ========================================
    // VARIANT SELECTION
    // ========================================
    /** When true, renders the AttachmentList variant (horizontal scrollable layout).
     * When false (default), renders the FileList variant (vertical list with grouping). */
    isAttachmentList: PropTypes.bool,

    // ========================================
    // COMMON PROPS (both variants)
    // ========================================
    /** Set accepted file types. Array of MIME types or file extensions that the component will accept. */
    accept: PropTypes.array,
    /** Object with custom styles classes. Allows for custom styling of the component beyond the default theme. */
    classes: PropTypes.object,
    /** Override component styles with a custom class. Useful for applying global styles to the component. */
    className: PropTypes.string,
    /** Aspect ratio for image cropping. Used when cropImages is true. (FileList only) */
    cropAspect: PropTypes.number,
    /** When true, allows cropping of image files. Displays a crop button for each image. (FileList only) */
    cropImages: PropTypes.bool,
    /** Text for the accept button in the crop modal. (FileList only) */
    cropAcceptLabel: PropTypes.string,
    /** Text for the cancel button in the crop modal. (FileList only) */
    cropCancelLabel: PropTypes.string,
    /** Title for the crop modal. (FileList only) */
    cropTitle: PropTypes.string,
    /** Tooltip text for the crop button. (FileList only) */
    cropTooltip: PropTypes.string,
    /** Tooltip text for the delete button. */
    deleteTooltip: PropTypes.string,
    /** When true, disables the file picker. User cannot select or drop files. */
    disabled: PropTypes.bool,
    /** Tooltip text for the download button. */
    downloadTooltip: PropTypes.string,
    /** Error message displayed below the component. Also triggers error styling when present. */
    error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    /** Array of files currently selected. Can be File objects or objects with id and file properties. */
    files: PropTypes.array,
    /** Function called when the download icon is clicked. Receives the file being downloaded. */
    handleDownload: PropTypes.func,
    /** Tooltip text displayed in an info icon next to the label. */
    hint: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    /** HTML id attribute for the hidden file input element. */
    id: PropTypes.string,
    /** Informational message displayed below the component. */
    info: PropTypes.string,
    /** When true, component will take up 100% of the available width. */
    isFullWidth: PropTypes.bool,
    /** When true, prevents user from selecting or removing files. The component appears non-interactive. */
    isReadOnly: PropTypes.bool,
    /** When true, displays an asterisk next to the label indicating the field is required. */
    isRequired: PropTypes.bool,
    /** Text to be displayed as the file picker's label. */
    label: PropTypes.string,
    /** Determines how the label is positioned relative to the file picker. */
    labelMode: PropTypes.oneOf(['horizontal', 'vertical']),
    /** Maximum accepted number of files. */
    maxFiles: PropTypes.number,
    /** Maximum file size in bytes. Files larger than this will be rejected. */
    maxSize: PropTypes.number,
    /** Minimum file size in bytes. Files smaller than this will be rejected. */
    minSize: PropTypes.number,
    /** Maximum image height in pixels. (FileList only) */
    maxHeight: PropTypes.number,
    /** Maximum image width in pixels. (FileList only) */
    maxWidth: PropTypes.number,
    /** Minimum image height in pixels. (FileList only) */
    minHeight: PropTypes.number,
    /** Minimum image width in pixels. (FileList only) */
    minWidth: PropTypes.number,
    /** When true, allows selecting multiple files at once. */
    multiple: PropTypes.bool,
    /** HTML name attribute for the hidden file input element. */
    name: PropTypes.string,
    /** Function called when an image is cropped. (FileList only) */
    onCrop: PropTypes.func,
    /** Function called when files are dropped or selected. */
    onDrop: PropTypes.func,
    /** Function called when dropped files exceed the maxFiles limit. */
    onExceedFileLimitDrop: PropTypes.func,
    /** Function called when a file is removed. */
    onRemove: PropTypes.func,
    /** Object with custom style overrides for inner elements. */
    overrides: PropTypes.object,
    /** When true, displays preview thumbnails for image files. */
    previewImages: PropTypes.bool,
    /** When true, displays a single image preview for the selected file (only works with maxFiles=1). (FileList only) */
    singleImagePreview: PropTypes.bool,

    // ========================================
    // FILE LIST VARIANT ONLY (isAttachmentList=false)
    // ========================================
    /** Label for the file selection button. Default is 'Select file'. (FileList only) */
    buttonLabel: PropTypes.string,
    /** Custom title component to replace the default text title. (FileList only) */
    customTitle: PropTypes.node,
    /** Additional data for files to be used for custom rendering or processing. (FileList only) */
    filesData: PropTypes.object,
    /** Text for the "Show more" button when files are folded. (FileList only) */
    foldedText: PropTypes.string,
    /** Defines groups of files with specific validation rules and limits. (FileList only) */
    groups: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            maxFiles: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            validateFiles: PropTypes.func,
        }),
    ),
    /** Maximum number of files to show before collapsing the list. (FileList only) */
    maxVisible: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** Additional informational text displayed below the title in the dropzone. (FileList only) */
    subtitle: PropTypes.string,
    /** Main title text displayed in the dropzone. Default is 'Drop files here'. (FileList only) */
    title: PropTypes.string,
    /** Text for the "Show less" button when files are unfolded. (FileList only) */
    unfoldedText: PropTypes.string,

    // ========================================
    // ATTACHMENT LIST VARIANT ONLY (isAttachmentList=true)
    // ========================================
    /** Show loading state on file items. (AttachmentList only) */
    isUploading: PropTypes.bool,
    /** When true, displays the upload icon in the dropzone. (AttachmentList only) */
    showIcon: PropTypes.bool,
};

// Expose sub-components for direct access if needed
FilePicker.FileList = FileList;
FilePicker.AttachmentList = AttachmentList;

export default FilePicker;
